import React from 'react';
import PropTypes from 'prop-types';
import Icon from 'components/Utils/NewIcon';
import CalendarUtils from 'services/CalendarUtils';
import PatientUtils from 'services/PatientUtils';
import StaffUtils from 'services/StaffUtils';
import './style.scss';
import UnreadBadge from 'components/Common/UnreadBadge';
import TaskUtils from 'services/TaskUtils';
import CheckIcon from 'components/Tasks/Utils/CheckIcon';
import TaskEditModal from 'components/Tasks/Utils/TaskEditModal';
import DefaultTooltip from 'components/Utils/DefaultTooltip';

const TaskBadgeView = (props) => {
  return (
    <>
      {props.editModal && (
        <TaskEditModal
          editModal={props.editModal}
          task={props.task}
          openEditModal={props.openEditModal}
          patient={props.patient}
          staff={props.staff}
          showPatient={props.showPatient}
        />
      )}
      <li className={getClassName(props.task, props.editable)}>
        <div className="task">
          <div className="icons">
            <div className="check">
              <CheckIcon task={props.task} onClick={props.onToggleStatus} />
            </div>

            {!!props.onArchive && (
              <div className="archive">
                <Icon
                  width="20px"
                  fontAwesomeStyle="far"
                  src="archive"
                  alt="Archive"
                  showTooltip={true}
                  onClick={props.onArchive}
                />
              </div>
            )}
            {props.isUnassigned && (
              <div className="claim">
                <Icon
                  width="20px"
                  fontAwesomeStyle="far"
                  src="user-plus"
                  tooltipPlace="top"
                  showTooltip={true}
                  alt="Claim"
                  onClick={props.onClaim}
                />
              </div>
            )}
          </div>

          <div className="info">
            <div className="title" onClick={props.onTitleClick}>
              {props.unreadComments > 0 && <UnreadBadge unreadCount={props.unreadComments} />}
              {props.unreadComments === 0 && props.isUnread && <UnreadBadge unreadText="!" />}
              <span className="title-text">{props.task.title}</span>
              {props.task.details !== undefined && props.task.details !== '' && (
                <Icon
                  width="13px"
                  src="note-sticky"
                  alt={props.task.details}
                  showTooltip={true}
                  tooltipPlace="bottom"
                  className="note-icon"
                />
              )}
            </div>

            <div className="user-wrapper">
              {props.patient && props.hidePatient !== true && (
                <div className="patient" onClick={props.onClickPatient}>
                  <Icon src="user" alt="Patient" height="13px" width="13px" />
                  <span>{PatientUtils.getName(props.patient, undefined, false)}</span>
                </div>
              )}

              <UserInfo user={props.assignedUser} icon="user-nurse-hair-long" alt="Staff" />

              {props.createdBy && (
                <div className="user">
                  <span className="creator-label">Author:</span>
                  <span>{StaffUtils.getFirstName(props.createdBy)}</span>
                </div>
              )}
            </div>

            <div className="time">
              <TimeDisplay task={props.task} />
            </div>
          </div>
        </div>
      </li>
    </>
  );
};

export const UserInfo = React.memo(({ user, icon, alt }) =>
  user ? (
    <div className="user">
      {icon && <Icon src={icon} alt={alt} />}
      <span>{StaffUtils.getFirstName(user)}</span>
    </div>
  ) : null,
);

const TimeDisplay = React.memo(({ task }) => {
  const hasDueDate = !!task.due_date;
  const hasRelevantDate = !!task.date;
  const isPastDue = hasDueDate && TaskUtils.isDue(task);
  const hasBothDates = hasDueDate && hasRelevantDate;

  return (
    <>
      {hasRelevantDate && (
        <DefaultTooltip title="Relevant by" placement="top">
          <span className="date-group">
            <Icon width="12px" src="calendar" alt="Relevant By" />
            <span>{CalendarUtils.format(task.date, 'Do MMMM YYYY')}</span>
          </span>
        </DefaultTooltip>
      )}
      {hasBothDates && <span className="date-separator">|</span>}
      {hasDueDate && (
        <DefaultTooltip title="Due by" placement="top">
          <span className={`date-group ${isPastDue ? 'red-text' : ''}`}>
            <Icon
              width="12px"
              src="alarm-exclamation"
              alt="Due Date"
              className={isPastDue ? 'red-icon' : ''}
            />
            <span>{CalendarUtils.format(task.due_date, 'Do MMMM YYYY')}</span>
          </span>
        </DefaultTooltip>
      )}
    </>
  );
});

function getClassName(task, editable) {
  let className = 'task-item';
  if (task.completed === true) className += ' completed';
  if (task.archived === true) className += ' archived';
  if (!editable) className += ' disabled';
  return className;
}

TaskBadgeView.propTypes = {
  task: PropTypes.object.isRequired,
  patient: PropTypes.object,
  createdBy: PropTypes.object,
  assignedUser: PropTypes.object,
  editable: PropTypes.bool,
  onTitleClick: PropTypes.func,
  onToggleStatus: PropTypes.func,
  onArchive: PropTypes.func,
  openEditModal: PropTypes.func,
  editModal: PropTypes.bool,
};

TaskBadgeView.defaultProps = {
  editable: false,
};

export default TaskBadgeView;
